import { useState, useEffect, useMemo } from 'react';
import uniqBy from 'lodash/uniqBy';

import { OrganizationStatus, OrganizationType } from 'chaika-enums';

import { AntdOptionType, makeAntdOptionType, makeOptionType } from '@utils/makeOptionTypes';
import { organizationService } from '../../service/organizaion/organizationService';
import { OrganizationFilterParams } from '../../store/organization/OrganizationList';
import { laboratoryService } from '@lab/service/laboratoryService';
import { OptionType } from '@components/Select';
import { translate } from '@chaika/i18n/helper';
import { store } from '@store';

interface UseOrganizationOptionTypesData {
    organizationOptions: AntdOptionType[];
    clinicOptions: AntdOptionType[];
    abbreviationOrganizationOptions: AntdOptionType[];
    contractorOptions: AntdOptionType[];
    legacyOrganizationOptions: OptionType<string>[];
    legacyContractorOptions: OptionType<string>[];
    getOptions(params?: OrganizationFilterParams): AntdOptionType[];
}

export function useOrganizationOptionTypes(withDefaultOption?: true): UseOrganizationOptionTypesData {
    const {
        organizationList,
        laboratoryStore: { laboratoryList },
    } = store;
    const defaultOption: AntdOptionType = { text: translate('notSet'), value: 'null' };

    const filterParams: OrganizationFilterParams = {
        status: OrganizationStatus.Active,
        types: [OrganizationType.Clinic, OrganizationType.ExternalClinic, OrganizationType.Laboratory],
    };

    const [organizationOptions, setOrganizationOptions] = useState<AntdOptionType[]>([]);
    const [contractorOptions, setContractorOptions] = useState<AntdOptionType[]>([]);
    const [clinicOptions, setClinicOptions] = useState<AntdOptionType[]>([]);

    const [legacyOrganizationOptions, setLegacyOrganizationOptions] = useState<OptionType<string>[]>([]);
    const [legacyContractorOptions, setLegacyContractorOptions] = useState<OptionType<string>[]>([]);

    useEffect(() => {
        (async () => {
            await Promise.all([organizationService.load(), laboratoryService.load()]);

            const organizationOptions: AntdOptionType[] = getOptions(filterParams);

            if (withDefaultOption) {
                organizationOptions.push(defaultOption);
            }

            setOrganizationOptions(organizationOptions);

            setClinicOptions([
                ...getOptions({
                    status: OrganizationStatus.Active,
                    types: [OrganizationType.Clinic],
                }),
                ...makeAntdOptionType(laboratoryList.values),
            ]);

            setLegacyOrganizationOptions(
                getLegacyOptions({
                    status: OrganizationStatus.Active,
                    types: [OrganizationType.Clinic, OrganizationType.ExternalClinic, OrganizationType.Laboratory],
                }),
            );

            setContractorOptions(
                uniqBy(
                    [
                        ...getOptions({
                            status: OrganizationStatus.Active,
                            types: [
                                OrganizationType.ExternalClinic,
                                OrganizationType.Laboratory,
                                OrganizationType.Organization,
                            ],
                        }),
                        ...makeAntdOptionType(laboratoryList.values),
                    ],
                    'value',
                ),
            );

            setLegacyContractorOptions([
                ...getLegacyOptions({
                    status: OrganizationStatus.Active,
                    types: [
                        OrganizationType.ExternalClinic,
                        OrganizationType.Laboratory,
                        OrganizationType.Organization,
                    ],
                }),
                ...makeOptionType(laboratoryList.values),
            ]);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function getOptions(params: OrganizationFilterParams = {}): AntdOptionType[] {
        return makeAntdOptionType(organizationList.getFilteredValues(params));
    }

    const abbreviationOrganizationOptions = useMemo((): AntdOptionType[] => {
        return makeAntdOptionType(
            organizationList.getFilteredValues(filterParams).map(item => ({
                id: item.id,
                name: item.abbreviation,
            })),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationList.values]);

    function getLegacyOptions(params: OrganizationFilterParams = {}): OptionType<string>[] {
        return makeOptionType(organizationList.getFilteredValues(params));
    }

    return {
        getOptions,
        clinicOptions,
        organizationOptions,
        abbreviationOrganizationOptions,
        contractorOptions,
        legacyOrganizationOptions,
        legacyContractorOptions,
    };
}
